// components/Compliance/MandatoryTrainingEditor.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, Save, PlayCircle } from 'lucide-react';
import {
  PageHeader, AppButton, Tabs, TabsList, TabsTrigger, TabsContent,
} from '../ui';
import {
  getCampaign, createCampaign, updateCampaign, activateCampaign,
  previewAudience, listGroups,
} from '../../utils/complianceApi';
import apiClient from '../../utils/apiClient';
import './_compliance.scss';

const EMPTY = {
  name: '',
  description: '',
  course: '',
  status: 'draft',
  audience: {
    allActiveStudents: false,
    groupIds: [],
    includeStudentIds: [],
    excludeStudentIds: [],
  },
  dueDate: '',
  reminderDaysBeforeDue: [7, 1],
  reminderDaysAfterDue: [1, 3],
  escalateToManagerAfterDays: 7,
  ccComplianceEmails: [],
  blocking: false,
  passingRequired: true,
};

const csv = (arr) => (arr || []).join(', ');
const fromCsvNumbers = (str) =>
  String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((n) => Number(n))
    .filter((n) => !Number.isNaN(n));
const fromCsvStrings = (str) =>
  String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export default function MandatoryTrainingEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('basic');

  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, g] = await Promise.all([
          apiClient.get('/self-paced/admin/courses/list').catch(() => ({ data: { courses: [] } })),
          listGroups().catch(() => ({ data: { groups: [] } })),
        ]);
        setCourses(c?.data?.courses || c?.data?.items || []);
        setGroups(g?.data?.groups || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await getCampaign(id);
        const camp = data?.campaign || {};
        setForm({
          ...EMPTY,
          ...camp,
          course: camp.course?._id || camp.course || '',
          dueDate: camp.dueDate ? String(camp.dueDate).slice(0, 10) : '',
          audience: { ...EMPTY.audience, ...(camp.audience || {}) },
        });
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateAudience = (patch) => setForm((f) => ({ ...f, audience: { ...f.audience, ...patch } }));

  const buildBody = () => ({
    name: form.name.trim(),
    description: form.description,
    course: form.course || undefined,
    audience: {
      allActiveStudents: !!form.audience.allActiveStudents,
      groupIds: form.audience.groupIds || [],
      includeStudentIds: form.audience.includeStudentIds || [],
      excludeStudentIds: form.audience.excludeStudentIds || [],
    },
    dueDate: form.dueDate || null,
    reminderDaysBeforeDue: form.reminderDaysBeforeDue || [],
    reminderDaysAfterDue: form.reminderDaysAfterDue || [],
    escalateToManagerAfterDays: Number(form.escalateToManagerAfterDays) || 0,
    ccComplianceEmails: form.ccComplianceEmails || [],
    blocking: !!form.blocking,
    passingRequired: !!form.passingRequired,
  });

  const validate = (body) => {
    if (!body.name) return 'Name is required.';
    if (!body.course) return 'Please select a course.';
    if (!body.dueDate) return 'Due date is required.';
    if (
      !body.audience.allActiveStudents &&
      !(body.audience.groupIds || []).length &&
      !(body.audience.includeStudentIds || []).length
    ) return 'Please select an audience.';
    return '';
  };

  const onSave = async () => {
    const body = buildBody();
    const err = validate(body);
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    try {
      if (isNew) {
        const { data } = await createCampaign(body);
        navigate(`/compliance/mandatory-training/${data?.campaign?._id || ''}`);
      } else {
        await updateCampaign(id, body);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const onActivate = async () => {
    if (isNew) { setError('Please save the campaign before activating.'); return; }
    setActivating(true); setError('');
    try {
      await activateCampaign(id);
      navigate(`/compliance/mandatory-training/${id}/report`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to activate.');
    } finally { setActivating(false); }
  };

  const onPreview = async () => {
    setPreviewLoading(true);
    try {
      const body = buildBody();
      const { data } = await previewAudience(body.audience);
      setAudiencePreview(data);
    } catch (e) {
      setAudiencePreview({ error: e?.response?.data?.message || 'Preview failed.' });
    } finally { setPreviewLoading(false); }
  };

  const groupOptions = useMemo(() => groups.map((g) => ({ id: g._id, name: g.name, type: g.type })), [groups]);

  if (loading) return <div className="cp-page">Loading…</div>;

  return (
    <div className="cp-page">
      <PageHeader
        title={isNew ? 'New Mandatory Training' : `Edit: ${form.name || 'Campaign'}`}
        icon={ShieldCheck}
        backButton
        onBack={() => navigate('/compliance/mandatory-training')}
        actions={
          <>
            <AppButton variant="outline" onClick={onSave} disabled={saving}>
              <Save size={16} /> {saving ? 'Saving…' : 'Save Draft'}
            </AppButton>
            {!isNew && form.status !== 'active' && (
              <AppButton variant="default" onClick={onActivate} disabled={activating}>
                <PlayCircle size={16} /> {activating ? 'Activating…' : 'Save & Activate'}
              </AppButton>
            )}
          </>
        }
      />

      <Tabs value={tab} onChange={setTab}>
        <TabsList>
          <TabsTrigger value="basic">Basics</TabsTrigger>
          <TabsTrigger value="audience">Audience</TabsTrigger>
          <TabsTrigger value="schedule">Schedule & Reminders</TabsTrigger>
          <TabsTrigger value="behavior">Behavior</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <div className="cp-form">
            <div className="cp-field">
              <label className="cp-field__label">Campaign Name</label>
              <input className="cp-input" value={form.name} onChange={(e) => update({ name: e.target.value })} />
            </div>
            <div className="cp-field">
              <label className="cp-field__label">Description</label>
              <textarea className="cp-textarea" value={form.description} onChange={(e) => update({ description: e.target.value })} />
            </div>
            <div className="cp-field">
              <label className="cp-field__label">Course</label>
              <select className="cp-select" value={form.course} onChange={(e) => update({ course: e.target.value })}>
                <option value="">— Select course —</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>{c.name || c.title}</option>
                ))}
              </select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audience">
          <div className="cp-form">
            <div className="cp-field">
              <label className="cp-field__label">
                <input
                  type="checkbox"
                  checked={!!form.audience.allActiveStudents}
                  onChange={(e) => updateAudience({ allActiveStudents: e.target.checked })}
                />{' '}
                Apply to all active employees
              </label>
              <div className="cp-field__hint">
                When enabled, group/include/exclude lists below act as overrides only.
              </div>
            </div>

            <div className="cp-field">
              <label className="cp-field__label">Employee Groups</label>
              <select
                className="cp-select"
                multiple
                value={form.audience.groupIds}
                onChange={(e) => updateAudience({
                  groupIds: Array.from(e.target.selectedOptions).map((o) => o.value),
                })}
              >
                {groupOptions.map((g) => (
                  <option key={g.id} value={g.id}>{g.name} ({g.type})</option>
                ))}
              </select>
              <div className="cp-field__hint">Hold Ctrl/⌘ to select multiple.</div>
            </div>

            <div className="cp-row">
              <div className="cp-field">
                <label className="cp-field__label">Include Specific Student IDs</label>
                <textarea
                  className="cp-textarea"
                  value={csv(form.audience.includeStudentIds)}
                  onChange={(e) => updateAudience({ includeStudentIds: fromCsvStrings(e.target.value) })}
                />
              </div>
              <div className="cp-field">
                <label className="cp-field__label">Exclude Specific Student IDs</label>
                <textarea
                  className="cp-textarea"
                  value={csv(form.audience.excludeStudentIds)}
                  onChange={(e) => updateAudience({ excludeStudentIds: fromCsvStrings(e.target.value) })}
                />
              </div>
            </div>

            <div className="cp-actions">
              <AppButton variant="outline" onClick={onPreview} disabled={previewLoading}>
                {previewLoading ? 'Previewing…' : 'Preview Audience'}
              </AppButton>
            </div>

            {audiencePreview && (
              <div className="cp-stat">
                <div className="cp-stat__label">Audience Preview</div>
                <div className="cp-stat__value">
                  {audiencePreview.error
                    ? audiencePreview.error
                    : `${audiencePreview.count ?? (audiencePreview.studentIds || []).length} employee(s) match`}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="cp-form">
            <div className="cp-row">
              <div className="cp-field">
                <label className="cp-field__label">Due Date</label>
                <input
                  type="date"
                  className="cp-input"
                  value={form.dueDate || ''}
                  onChange={(e) => update({ dueDate: e.target.value })}
                />
              </div>
              <div className="cp-field">
                <label className="cp-field__label">Escalate to Manager After (days overdue)</label>
                <input
                  type="number" min="0"
                  className="cp-input"
                  value={form.escalateToManagerAfterDays}
                  onChange={(e) => update({ escalateToManagerAfterDays: e.target.value })}
                />
                <div className="cp-field__hint">Set to 0 to disable manager escalation.</div>
              </div>
            </div>

            <div className="cp-row">
              <div className="cp-field">
                <label className="cp-field__label">Reminder Days Before Due (comma separated)</label>
                <input
                  className="cp-input"
                  value={csv(form.reminderDaysBeforeDue)}
                  onChange={(e) => update({ reminderDaysBeforeDue: fromCsvNumbers(e.target.value) })}
                  placeholder="7, 3, 1"
                />
              </div>
              <div className="cp-field">
                <label className="cp-field__label">Reminder Days After Due (comma separated)</label>
                <input
                  className="cp-input"
                  value={csv(form.reminderDaysAfterDue)}
                  onChange={(e) => update({ reminderDaysAfterDue: fromCsvNumbers(e.target.value) })}
                  placeholder="1, 3, 7"
                />
              </div>
            </div>

            <div className="cp-field">
              <label className="cp-field__label">CC Compliance Emails (comma separated)</label>
              <input
                className="cp-input"
                value={csv(form.ccComplianceEmails)}
                onChange={(e) => update({ ccComplianceEmails: fromCsvStrings(e.target.value) })}
                placeholder="compliance@company.com"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="behavior">
          <div className="cp-form">
            <div className="cp-field">
              <label className="cp-field__label">
                <input
                  type="checkbox"
                  checked={!!form.blocking}
                  onChange={(e) => update({ blocking: e.target.checked })}
                />{' '}
                Block access to other learning until this training is completed
              </label>
              <div className="cp-field__hint">
                Recommended for legal / safety / compliance training.
              </div>
            </div>
            <div className="cp-field">
              <label className="cp-field__label">
                <input
                  type="checkbox"
                  checked={!!form.passingRequired}
                  onChange={(e) => update({ passingRequired: e.target.checked })}
                />{' '}
                Require a passing score (not just completion)
              </label>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {error && (
        <div className="cp-stat cp-error" role="alert">
          <div className="cp-stat__label">Error</div>
          <div className="cp-stat__value">{error}</div>
        </div>
      )}
    </div>
  );
}
